import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Empty } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { MessageSquare, Plus, RefreshCw, Loader2, CheckCircle2, Search, X, Eye } from 'lucide-react';
import { useAvatarsList } from '../../hooks/useAvatarsList.js';
import { useTenantDids } from '../../hooks/useTenantDids.js';
import { useTenant } from '../../components/TenantContext.jsx';
import { useBuildJobs, computeBuildProgress } from '../../components/IvrAgents/BuildJobsContext.jsx';
import { AvatarCard } from '../../components/IvrAgents/AvatarCard.jsx';
import { AddAvatarWizard } from '../../components/IvrAgents/AddAvatarWizard.jsx';
import { BuildLogModal } from '../../components/IvrAgents/BuildLogModal.jsx';
import { auth } from '../../auth/firebase.js';

const CATEGORIES = [
    { value: '', labelKey: 'common:state.all' },
    { value: 'published', labelKey: 'filter.published' },
    { value: 'unpublished', labelKey: 'filter.unpublished' },
    { value: 'transfer-only', labelKey: 'filter.transferOnly' },
    { value: 'full', labelKey: 'filter.full' },
    { value: 'survey', labelKey: 'filter.survey' },
];

const PAGE_SIZE = 25;   // 一次載入量

// 頁碼窗：回 [1, '…', 4, 5, 6, '…', 40] 這種陣列（current 周圍 ±2 + 頭尾），與外撥名單一致
function buildPageWindow(current, count) {
    if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
    const out = [1];
    const lo = Math.max(2, current - 2);
    const hi = Math.min(count - 1, current + 2);
    if (lo > 2) out.push('…');
    for (let p = lo; p <= hi; p++) out.push(p);
    if (hi < count - 1) out.push('…');
    out.push(count);
    return out;
}

function matchCategory(a, cat) {
    if (!cat) return true;
    if (cat === 'published')    return a.enabled === true;
    if (cat === 'unpublished')  return a.enabled !== true;
    if (cat === 'survey')       return a.surveyMode === true;
    if (cat === 'transfer-only') return a.flowMode === 'transfer-only' && a.surveyMode !== true;
    if (cat === 'full')         return (a.flowMode || 'full') === 'full' && a.surveyMode !== true;
    return true;
}

export default function IvrAgentsListPage() {
    const { t } = useTranslation('ivrAgents');
    const { hostId, isAdmin } = useTenant();
    const { avatars, loading, error, reload, optimisticUpdate, optimisticRemove } = useAvatarsList(hostId);
    // 「已綁/未綁門號」標籤的資料來源 —— 進線路由，不是 avatar.did（見 useTenantDids.js）
    const didsByAvatar = useTenantDids(hostId);
    const { jobs, remove: removeJob } = useBuildJobs();
    // 🔒 只顯示「目前這個 tenant」的建構卡 —— BuildJobsProvider 是 App 層全域的，
    // 沒這道過濾切 tenant 後還會看到別家的卡（2026-08-10 東訊看到 aa-test01「qq」的案例）。
    const buildJobs = Object.values(jobs).filter(j => j.type === 'build' && (!j.hostId || !hostId || j.hostId === hostId));
    const [wizardOpen, setWizardOpen] = useState(false);
    const [search, setSearch] = useState('');         // 名稱關鍵字搜尋
    const [category, setCategory] = useState('');     // 分類 tab
    const [page, setPage] = useState(1);  // 分頁：目前頁碼（1-based，與外撥名單一致）
    // 換分類 / 改搜尋字 → 重置分頁回第一頁
    useEffect(() => { setPage(1); }, [category, search]);
    // Set of avatarIds with running update (來自 /api/ivr/sessions/active) — 顯示 in-progress badge
    const [inProgressAvatarIds, setInProgressAvatarIds] = useState(new Set());

    // 輪詢 /sessions/active 每 5s，看哪些 avatar 還在跑 update
    useEffect(() => {
        let cancelled = false;
        async function fetchActive() {
            try {
                const idToken = await auth.currentUser?.getIdToken();
                if (!idToken) return;
                const r = await fetch(`/ivr-builder/api/ivr/sessions/active?hostId=${encodeURIComponent(hostId || '')}`, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                if (!r.ok || cancelled) return;
                const data = await r.json();
                const ids = new Set((data.sessions || []).map(s => s.avatarId).filter(Boolean));
                setInProgressAvatarIds(ids);
            } catch { /* ignore network blips */ }
        }
        if (hostId) fetchActive();
        const t = setInterval(() => { if (hostId) fetchActive(); }, 5000);
        return () => { cancelled = true; clearInterval(t); };
    }, [hostId]);

    // build job 完成（avatarId 出現）→ 自動 reload list
    useEffect(() => {
        const justCompleted = buildJobs.find(j => j.status === 'completed' && j.avatarId);
        if (justCompleted) reload({ refresh: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildJobs.map(j => j.status + j.avatarId).join('|')]);

    // 完成的 job 8s 後自動 dismiss — 避免 avatar id mismatch / enable filter 卡卡讓卡片一直停在那
    useEffect(() => {
        const timers = [];
        for (const j of buildJobs) {
            if (j.status !== 'completed') continue;
            // 已在真實 avatar list 出現 → 卡片本來就會被 filter 掉，不用 timer
            if (avatars.some(a => a.avatarId === j.avatarId)) continue;
            timers.push(setTimeout(() => removeJob(j.jobId), 8000));
        }
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildJobs.map(j => j.status + j.avatarId).join('|'), avatars.length]);

    // 子卡若 client-side 補 seed 寫回後端成功 → optimistic 把 seed 寫進本地 state
    const handleCoverSeedAssigned = (avatarId, seed) => {
        optimisticUpdate(avatarId, { coverSeed: seed });
    };

    // toggle / rename / 其他 PATCH 完成 → optimistic 改 local，不重 fetch（避免 UI 閃爍）
    const handleAvatarUpdated = (avatarId, patch) => {
        optimisticUpdate(avatarId, patch);
    };

    // 刪除：optimistic 從 list 拿掉，不重 fetch（後端已 soft-delete + 清 cache）
    const handleAvatarDeleted = (avatarId) => {
        optimisticRemove(avatarId);
    };

    // 套完「建構中排除 + 分類 + 搜尋」三層過濾後的完整清單（分頁前）
    const filteredAvatars = avatars
        .filter((a) => !buildJobs.some(j => j.status === 'running' && j.name === a.name))
        .filter((a) => matchCategory(a, category))
        .filter((a) => !search.trim() || (a.name || '').toLowerCase().includes(search.trim().toLowerCase()));
    // 搜尋時不受分頁限制 — 直接顯示整個清單的所有 match（搜尋已自行收斂結果）
    const isSearching = !!search.trim();
    // 搜尋時顯示全部 match（不分頁）；否則用數字頁碼分頁（與外撥名單一致）
    const pageCount = Math.max(1, Math.ceil(filteredAvatars.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const visibleAvatars = isSearching
        ? filteredAvatars
        : filteredAvatars.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return (
        <div>
            <PageHeader
                eyebrow="IVR Agents"
                title={t('page.title')}
                description={isAdmin ? t('page.descAdmin') : t('page.descReadOnly')}
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="md" onClick={() => reload({ refresh: true })}>
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                            {t('common:actions.refresh')}
                        </Button>
                        {isAdmin && (
                            <Button variant="accent" size="md" onClick={() => setWizardOpen(true)}>
                                <Plus size={14} />
                                {t('page.addAgent')}
                            </Button>
                        )}
                    </div>
                }
            />

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">
                        {t('page.loadFailed', { message: error.message })}
                    </div>
                </Card>
            )}

            {/* 分類 + 名稱搜尋 */}
            {avatars.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    {CATEGORIES.map((c) => {
                        const count = c.value
                            ? avatars.filter((a) => matchCategory(a, c.value)).length
                            : avatars.length;
                        return (
                            <button
                                key={c.value}
                                onClick={() => setCategory(c.value)}
                                className={[
                                    'px-3 py-1 rounded-full text-xs font-medium transition',
                                    category === c.value
                                        ? 'bg-ink-900 text-cream-50'
                                        : 'bg-cream-100 text-ink-700 hover:bg-cream-200',
                                ].join(' ')}
                            >
                                {t(c.labelKey)}
                                <span className={['ml-1 tnum', category === c.value ? 'text-cream-200' : 'text-ink-400'].join(' ')}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                    <div className="relative ml-auto max-w-xs w-full sm:w-auto">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('filter.searchPlaceholder')}
                            className="w-full pl-9 pr-8 py-2 text-sm border border-cream-300 rounded-lg bg-white text-ink-800 placeholder:text-ink-400 focus:outline-none focus:border-accent-400"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                                aria-label={t('filter.clear')}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {loading ? (
                <Card>
                    <div className="text-center text-ink-400 text-sm py-6">{t('common:state.loading')}</div>
                </Card>
            ) : (avatars.length === 0 && buildJobs.length === 0) ? (
                <Card>
                    <Empty
                        icon={MessageSquare}
                        title={isAdmin ? t('empty.adminTitle') : t('empty.readOnlyTitle')}
                        description={isAdmin ? t('empty.adminDesc') : t('empty.readOnlyDesc')}
                        action={isAdmin ? (
                            <Button variant="accent" onClick={() => setWizardOpen(true)}>
                                <Plus size={14} /> {t('page.addAgent')}
                            </Button>
                        ) : null}
                    />
                </Card>
            ) : (
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {/* 建構中的 placeholder card（背景跑的 build） */}
                    {buildJobs.filter(j => j.status === 'running' || (j.status === 'completed' && !avatars.find(a => a.avatarId === j.avatarId))).map(j => (
                        <BuildingCard key={j.jobId} job={j} onDismiss={() => removeJob(j.jobId)} />
                    ))}
                    {/* 真實 avatar 卡 — 排除「正在建構中的同名 avatar」(create_avatar.js 已建但 finalize.js 尚未完成)，再套分頁 */}
                    {visibleAvatars.map((a) => (
                        <AvatarCard
                            key={a.avatarId}
                            avatar={a}
                            dids={didsByAvatar === null ? null : (didsByAvatar[a.avatarId] || [])}
                            readOnly={!isAdmin}
                            inProgress={inProgressAvatarIds.has(a.avatarId)}
                            onCoverSeedAssigned={handleCoverSeedAssigned}
                            onUpdated={handleAvatarUpdated}
                            onDeleted={handleAvatarDeleted}
                        />
                    ))}
                </div>
            )}

            {/* 數字頁碼分頁（與外撥名單一致）— 非搜尋且超過一頁時顯示 */}
            {!loading && !isSearching && pageCount > 1 && (
                <div className="mt-6 flex items-center justify-center gap-1 text-xs">
                    <span className="text-ink-400 tnum mr-2">
                        {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredAvatars.length)} / {filteredAvatars.length}
                    </span>
                    <button disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="px-2 py-1 rounded border border-cream-300 bg-white disabled:opacity-40 hover:bg-cream-100">‹</button>
                    {buildPageWindow(safePage, pageCount).map((p, i) => p === '…' ? (
                        <span key={`e${i}`} className="px-1 text-ink-400">…</span>
                    ) : (
                        <button key={p} onClick={() => setPage(p)}
                            className={`min-w-[28px] px-2 py-1 rounded border tabular-nums ${p === safePage ? 'border-accent-500 bg-accent-500 text-white' : 'border-cream-300 bg-white hover:bg-cream-100 text-ink-700'}`}>{p}</button>
                    ))}
                    <button disabled={safePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        className="px-2 py-1 rounded border border-cream-300 bg-white disabled:opacity-40 hover:bg-cream-100">›</button>
                </div>
            )}

            {/* 篩選無結果提示（分類 + 搜尋疊加後沒任何 match）*/}
            {!loading && avatars.length > 0 && (category || search.trim())
                && !avatars.some((a) =>
                    matchCategory(a, category)
                    && (!search.trim() || (a.name || '').toLowerCase().includes(search.trim().toLowerCase()))
                ) && (
                <Card>
                    <div className="text-center text-ink-400 text-sm py-6">
                        {search.trim() && category
                            ? t('filter.noResultCategoryAndSearch', { category: t(CATEGORIES.find(c => c.value === category)?.labelKey || 'common:state.all'), search: search.trim() })
                            : search.trim()
                                ? t('filter.noResultSearch', { search: search.trim() })
                                : t('filter.noResultCategory', { category: t(CATEGORIES.find(c => c.value === category)?.labelKey || 'common:state.all') })}
                    </div>
                </Card>
            )}

            <AddAvatarWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onBuilt={() => reload({ refresh: true })}
            />
        </div>
    );
}

const TOOL_LABEL_KEYS = {
    'build-ivr-avatar': 'tool.buildAvatar',
    'build-kb-from-files': 'tool.buildKb',
    'sync-ivr-config': 'tool.syncConfig',
    'generate-ivr-greeting': 'tool.generateGreeting',
    'vectorize-ivr-kb': 'tool.vectorizeKb',
    'parse-api-docs': 'tool.parseApiDocs',
    'Bash': 'tool.bash',
    'Read': 'tool.read',
    'Write': 'tool.write',
    'Edit': 'tool.edit',
    'Glob': 'tool.glob',
    'Grep': 'tool.grep',
    'WebFetch': 'tool.webFetch',
};

function toolLabel(name, t) {
    return TOOL_LABEL_KEYS[name] ? t(TOOL_LABEL_KEYS[name]) : t('event.runTool', { name });
}

function describeEvent(ev, t) {
    if (!ev) return '';
    const type = ev.type;
    if (type === 'tool_start' && ev.name) return toolLabel(ev.name, t);
    if (type === 'text_delta' && ev.text) return String(ev.text).substring(0, 60);
    if (type === 'thinking_delta' && ev.text) return t('event.thinkingDelta', { text: String(ev.text).substring(0, 50) });
    if (type === 'thinking_start') return t('event.thinking');
    if (type === 'text_start') return t('event.responding');
    if (type === 'tool_input_delta') return t('event.preparingToolArgs');
    if (type === 'tool_result' && ev.content) return t('event.toolDone', { count: String(ev.content).length });
    if (type === 'log' && ev.message) return String(ev.message).substring(0, 60);
    if (type === 'init') return t('event.init');
    if (type === 'started') return t('event.started');
    if (type === 'result') return ev.status === 'failed' ? t('event.buildFailed') : t('event.buildDone');
    if (type === 'complete') return t('event.complete');
    if (type === 'final') return t('event.finalizing');
    return type || 'event';
}

// 主狀態：找最近 tool_start（穩定的階段指示器）
function getCurrentStage(events, t) {
    for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i];
        if (ev.type === 'tool_start' && ev.name) {
            return toolLabel(ev.name, t);
        }
    }
    // 沒 tool_start → 用最新 event 的描述
    return describeEvent(events[events.length - 1], t);
}

function BuildingCard({ job, onDismiss }) {
    const { t } = useTranslation('ivrAgents');
    const isDone = job.status === 'completed' && job.avatarId;
    const isFailed = job.status === 'failed';
    // Tick every second so the time-based ramp inside computeBuildProgress (for long Bash tools
    // like build_kb_hybrid) advances smoothly without waiting for new SSE events.
    const [, setTick] = useState(0);
    const [logOpen, setLogOpen] = useState(false);
    useEffect(() => {
        if (isDone || isFailed) return;
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, [isDone, isFailed]);
    const { percent, stage, stageParams } = computeBuildProgress(job.events);
    const stageText = stage ? t(stage, stageParams) : '';
    const eventCount = job.events.length;

    return (
        <div className="relative bg-cream-100/60 border border-cream-300 border-dashed rounded-lg overflow-hidden">
            {/* 看 log 按鈕 — 建構中才出現 */}
            {!isDone && !isFailed && (
                <button
                    onClick={() => setLogOpen(true)}
                    title={t('building.viewLogTitle')}
                    className="absolute top-2 right-2 z-10 size-7 inline-flex items-center justify-center rounded-md bg-cream-50/90 backdrop-blur text-ink-500 hover:text-accent-700 hover:bg-cream-50 border border-cream-300 transition"
                >
                    <Eye size={14} strokeWidth={1.8} />
                </button>
            )}
            <BuildLogModal open={logOpen} job={job} onClose={() => setLogOpen(false)} />
            {/* Top zone — 模擬封面 */}
            <div className="aspect-[2/1] bg-gradient-to-br from-cream-200/60 to-cream-300/40 flex items-center justify-center relative">
                {isDone ? (
                    <CheckCircle2 size={36} strokeWidth={1.4} className="text-green-700" />
                ) : isFailed ? (
                    <div className="text-red-600 font-mono text-xs">failed</div>
                ) : (
                    <Loader2 size={32} strokeWidth={1.4} className="text-accent-700 animate-spin" />
                )}
            </div>
            {/* Bottom info */}
            <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-400">
                        {isDone ? 'built' : isFailed ? 'failed' : 'building'}
                    </div>
                    {!isDone && !isFailed && (
                        <span className="font-mono text-[10px] text-accent-700 tabular-nums">
                            <span className="font-semibold">{percent}%</span>
                            <span className="text-ink-400 ml-2">{eventCount} events</span>
                        </span>
                    )}
                </div>
                <div className="heading-display text-base font-semibold text-ink-900 truncate">
                    {job.name}
                </div>

                {/* Progress bar */}
                {!isDone && !isFailed && (
                    <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-accent-500 transition-all duration-500 ease-out"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                )}

                <div className="text-[11px] text-ink-500 italic truncate min-h-[14px]" title={stageText}>
                    {isFailed
                        ? (job.error || t('event.buildFailed'))
                        : isDone
                            ? t('building.doneRefreshing')
                            : (stageText || (eventCount > 0 ? t('building.processing', { count: eventCount }) : t('building.preparing')))}
                </div>

                {(isDone || isFailed) && (
                    <button
                        onClick={onDismiss}
                        className="mt-1 text-[11px] text-ink-400 hover:text-ink-700 underline underline-offset-2"
                    >
                        {t('common:actions.close')}
                    </button>
                )}
            </div>
        </div>
    );
}
